'use client';

import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { createClient } from '@/lib/supabase/client';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  order_position: number;
  is_active: boolean;
}

export default function KSeFFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [ksefFaqItems, setKsefFaqItems] = useState<FAQItem[]>([]);
  const supabase = createClient();

  useEffect(() => {
    const fetchFAQItems = async () => {
      try {
        const { data, error } = await supabase
          .from('faq_ksef_items')
          .select('*')
          .eq('is_active', true)
          .order('order_position', { ascending: true });

        if (error) {
          console.error('Error fetching KSeF FAQ items:', error.message);
        } else {
          setKsefFaqItems(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchFAQItems();
  }, [supabase]);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="ksef-faq" className="py-20 md:py-28 bg-offwhite">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 space-y-3">
          <p className="section-label">FAQ KSeF</p>
          <h2 className="text-4xl md:text-5xl font-900 tracking-tight text-navy leading-[0.95]">
            FAQ: Pakiet Online – KSeF i Cyfryzacja Biura
          </h2>
        </div>

        {/* FAQ list */}
        <div>
          {ksefFaqItems.map((item, i) => (
            <KSeFFAQItem
              key={item.id}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function KSeFFAQItem({
  item,
  index,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { elementRef, isVisible } = useScrollAnimation();

  return (
    <div
      ref={elementRef}
      className={`faq-item transition-all duration-700 ease-out ${
        isVisible
          ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: `${index * 100}ms`,
      }}
    >
      <button
        className="faq-question"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`ksef-faq-answer-${index}`}
      >
        <span className="pr-4">{item.question}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={`faq-icon ${isOpen ? 'open' : ''} text-blue-DEFAULT`}
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      <div
        id={`ksef-faq-answer-${index}`}
        className={`faq-answer ${isOpen ? 'open' : ''}`}
        role="region"
      >
        <p className="text-base text-muted font-500 leading-relaxed">
          {item.answer}
        </p>
      </div>
    </div>
  );
}